import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import Logo from '@/components/branding/Logo';

const COMPLETED_STEPS = [
  'Your name',
  'Interview date',
  'English level',
  'Focus areas',
  'Try a question',
  'Choose your plan',
];

interface OnboardingCompleteShellProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  eyebrow?: ReactNode;
  children: ReactNode;
}

export default function OnboardingCompleteShell({
  title,
  subtitle,
  eyebrow,
  children,
}: OnboardingCompleteShellProps) {
  return (
    <div className="min-h-screen bg-cream lg:grid lg:grid-cols-[420px_1fr] xl:grid-cols-[480px_1fr]">
      {/* Desktop side rail — celebration */}
      <aside className="hidden lg:flex relative overflow-hidden bg-gradient-to-b from-cream-warm to-cream-light border-r border-border">
        <div className="absolute -top-32 -right-24 h-80 w-80 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-accent/15 blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col w-full px-12 py-16 xl:px-16">
          <div className="mb-16">
            <Logo variant="horizontal" size="md" />
          </div>

          <div className="mb-10">
            <div className="font-sans text-xs text-text-muted uppercase tracking-widest mb-2">
              All set
            </div>
            <div className="font-display italic font-light text-3xl xl:text-4xl text-text-primary leading-tight">
              Welcome to your prep.
            </div>
          </div>

          <ol className="space-y-1">
            {COMPLETED_STEPS.map((label, i) => (
              <li key={label} className="flex items-start gap-4 py-2">
                <div className="relative flex flex-col items-center pt-0.5">
                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-success text-white">
                    <Check size={14} strokeWidth={3} />
                  </div>
                  {i < COMPLETED_STEPS.length - 1 && (
                    <div className="w-px h-7 mt-1 bg-success/25" />
                  )}
                </div>
                <div className="pb-2">
                  <div className="font-sans font-medium text-sm leading-tight text-text-primary">
                    {label}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </aside>

      {/* Right column — celebration */}
      <main className="relative flex flex-col items-center justify-center px-6 py-8 md:px-12 md:py-12 lg:py-20 lg:px-16 xl:px-24">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 hidden md:block"
          style={{
            background:
              'radial-gradient(ellipse 900px 500px at 30% 0%, rgba(212, 163, 115, 0.18), transparent 60%)',
          }}
        />
        <div className="relative w-full max-w-md lg:max-w-xl text-center">
          {eyebrow && (
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-sans text-xs uppercase tracking-[0.2em] text-text-muted mb-3"
            >
              {eyebrow}
            </motion.p>
          )}

          {title && (
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="font-display italic font-light text-5xl md:text-6xl text-text-primary leading-[1.05] mb-5"
            >
              {title}
            </motion.h1>
          )}

          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="font-sans text-base md:text-lg text-text-secondary mb-10 leading-relaxed"
            >
              {subtitle}
            </motion.p>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            {children}
          </motion.div>
        </div>
      </main>
    </div>
  );
}
